import { Alert, Box, CircularProgress, Paper, Typography } from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import Button from "../../../../shared/OAWorkflow/components/atoms/Button";
import { SUMMARY_TILES } from "../constants";

/**
 * The counts to reason about, with anything the response left out read as zero. Without
 * this a single missing count turns every comparison below false and puts a literal "NaN"
 * in front of the user, next to tiles showing the right numbers.
 */
const countsOf = (summary = {}) => ({
  totalRows: summary.totalRows || 0,
  successCount: summary.successCount || 0,
  partialCount: summary.partialCount || 0,
  failedCount: summary.failedCount || 0,
});

/**
 * Whether every row in the file made it. Measured against `totalRows` rather than against
 * `failedCount` alone: a row the server dropped without counting it as failed - a duplicate
 * reported only in `duplicateCount`, say - still means the file did not import cleanly, and
 * calling that a success would contradict the duplicate note rendered right below it.
 */
const isCleanImport = ({ totalRows, successCount, partialCount, failedCount }) =>
  totalRows > 0 && failedCount === 0 && partialCount === 0 && successCount === totalRows;

/** Which of the three outcomes an upload as a whole landed on. */
const overallSeverity = (summary) => {
  const counts = countsOf(summary);
  if (isCleanImport(counts)) return "success";
  // An empty file is not a failure to fix, so it reads as a warning rather than an error.
  if (counts.totalRows === 0) return "warning";
  return counts.successCount + counts.partialCount > 0 ? "warning" : "error";
};

const overallMessage = (summary) => {
  const counts = countsOf(summary);
  const { totalRows, successCount, partialCount } = counts;
  const imported = successCount + partialCount;
  if (totalRows === 0) return "No inventor rows were found in this file.";
  if (isCleanImport(counts)) return `All ${successCount} inventor rows were imported.`;
  if (imported === 0) {
    return "No rows could be imported. Download the validation result to see why.";
  }
  return `${imported} of ${totalRows} rows were imported. Download the validation result to review the rest.`;
};

const SummaryTile = ({ label, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      flex: "1 1 160px",
      minWidth: 140,
      p: 2,
      border: "1px solid #E0E0E0",
      borderRadius: 2,
      backgroundColor: "#fff",
    }}
  >
    <Typography variant="h4" sx={{ color, fontWeight: 600, mb: 0.5 }}>
      {value}
    </Typography>
    <Typography variant="body2" sx={{ color: "#64748B" }}>
      {label}
    </Typography>
  </Paper>
);

/**
 * The outcome of an upload: the four counts KOT-107 asks for, plus the link to the
 * downloadable validation result. Duplicates sit under Failed rather than as a fifth
 * total, since a duplicate row is one particular reason a row failed.
 */
const UploadSummary = ({ summary, onDownloadResult, downloadingResult, downloadError }) => {
  return (
    <Box sx={{ mt: 4 }} data-testid="inventor-bulk-upload-summary">
      <Typography variant="h5" sx={{ color: "#1E293B", fontSize: "20px", fontWeight: 400, mb: 2 }}>
        Upload Summary
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
        {SUMMARY_TILES.map((tile) => (
          <SummaryTile key={tile.key} label={tile.label} value={summary[tile.key] ?? 0} color={tile.color} />
        ))}
      </Box>

      {summary.duplicateCount > 0 && (
        <Typography variant="body2" sx={{ color: "#64748B", mb: 2 }}>
          {summary.duplicateCount} of the failed {summary.duplicateCount === 1 ? "row was" : "rows were"} skipped
          because a matching inventor already exists.
        </Typography>
      )}

      <Alert
        severity={overallSeverity(summary)}
        sx={{ borderRadius: 1.5, fontWeight: 500 }}
        action={
          // No batch id means there is no result to fetch - offering the button anyway
          // would request /batches/undefined/result and report a 404 over a success.
          summary.batchId ? (
            <Button
              variant="secondary"
              startIcon={downloadingResult ? <CircularProgress size={16} color="inherit" /> : <GetAppIcon />}
              onClick={() => onDownloadResult(summary.batchId)}
              disabled={downloadingResult}
              sx={{ border: "none", color: "inherit" }}
            >
              Download Validation Result
            </Button>
          ) : null
        }
      >
        {overallMessage(summary)}
      </Alert>

      {downloadError && (
        <Alert severity="error" sx={{ mt: 2, borderRadius: 1.5 }}>
          {downloadError}
        </Alert>
      )}

    </Box>
  );
};

export default UploadSummary;
